import { useCallback, useEffect, useRef, useState } from 'react';

// Borrador automático de formularios: lo que el admin escribe se guarda en este
// navegador mientras edita, y si sale de la pantalla y regresa se recupera.
// Se borra al guardar (clear) o al descartarlo (discard).
// Nunca guarda secretos: pasa en `omit` los campos sensibles (token, password…);
// los archivos (File) se omiten siempre.

const PREFIX = 'draft:';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const isPlainObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

/**
 * @param {string} key      Identificador del formulario (incluye el id del registro si se edita).
 * @param {any} value       Estado actual del formulario.
 * @param {Function} setValue Setter del estado.
 * @param {{ ready?: boolean, omit?: string[] }} opts
 *   ready: true cuando el formulario ya cargó sus datos iniciales (en edición, después de llenar desde la BD).
 */
export function useDraft(key, value, setValue, { ready = true, omit = [] } = {}) {
  const storageKey = PREFIX + key;
  const baseline = useRef(null);
  const [restoredAt, setRestoredAt] = useState(null);
  const omitRef = useRef(omit);
  omitRef.current = omit;

  const strip = useCallback((v) => {
    if (!isPlainObject(v)) return v;
    const out = {};
    Object.entries(v).forEach(([k, val]) => {
      if (omitRef.current.includes(k)) return;
      if (typeof File !== 'undefined' && val instanceof File) return;
      out[k] = val;
    });
    return out;
  }, []);

  // Recuperar una sola vez, cuando el formulario está listo
  useEffect(() => {
    if (!ready || baseline.current !== null) return;
    baseline.current = JSON.stringify(strip(value));
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft?.savedAt || Date.now() - draft.savedAt > MAX_AGE_MS) {
        localStorage.removeItem(storageKey);
        return;
      }
      if (JSON.stringify(draft.value) === baseline.current) return;
      setValue((prev) => (isPlainObject(prev) && isPlainObject(draft.value) ? { ...prev, ...draft.value } : draft.value));
      setRestoredAt(draft.savedAt);
    } catch {
      localStorage.removeItem(storageKey);
    }
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guardar mientras se edita (si no hay cambios contra lo cargado, no hay borrador)
  useEffect(() => {
    if (!ready || baseline.current === null) return undefined;
    const t = setTimeout(() => {
      try {
        const current = JSON.stringify(strip(value));
        if (current === baseline.current) localStorage.removeItem(storageKey);
        else localStorage.setItem(storageKey, JSON.stringify({ savedAt: Date.now(), value: strip(value) }));
      } catch { /* almacenamiento lleno o bloqueado: no es crítico */ }
    }, 400);
    return () => clearTimeout(t);
  }, [value, ready, storageKey, strip]);

  /** Llamar después de guardar con éxito. */
  const clear = useCallback(() => {
    localStorage.removeItem(storageKey);
    baseline.current = JSON.stringify(strip(value));
    setRestoredAt(null);
  }, [storageKey, strip, value]);

  /** Volver a lo que estaba guardado antes del borrador. */
  const discard = useCallback(() => {
    localStorage.removeItem(storageKey);
    if (baseline.current !== null) {
      const base = JSON.parse(baseline.current);
      setValue((prev) => (isPlainObject(prev) && isPlainObject(base) ? { ...prev, ...base } : base));
    }
    setRestoredAt(null);
  }, [storageKey, setValue]);

  return { restoredAt, clear, discard };
}
